import { useEffect, useState } from 'react';
import { BookOpen, FileText, Folder, Home, X } from 'lucide-react';
import type { LexCounselWorkspaceSnapshot } from '../../store/lexCounselStore';
import type { WorkspaceScreen } from '../../lib/workspaceContext';
import { useLexCounselStore } from '../../store/lexCounselStore';

function sharingLabelFor(workspace: LexCounselWorkspaceSnapshot): string | null {
  if (workspace.screen === 'draft_editor') {
    const name =
      typeof workspace.draftTitle === 'string' ? workspace.draftTitle.trim() : '';
    return name || 'Current draft';
  }
  if (workspace.screen === 'case') {
    const title = workspace.caseTitle;
    if (typeof title === 'string' && title.trim()) return title;
    return 'Current case';
  }
  if (workspace.screen === 'library') return 'Statute library';
  if (workspace.screen === 'dashboard') return 'Dashboard';
  return null;
}

function sharingIconFor(screen: WorkspaceScreen) {
  switch (screen) {
    case 'draft_editor':
      return FileText;
    case 'case':
      return Folder;
    case 'library':
      return BookOpen;
    case 'dashboard':
      return Home;
    default:
      return FileText;
  }
}

export function useLexCounselSharing() {
  const workspace = useLexCounselStore((s) => s.workspace);
  const [dismissed, setDismissed] = useState(false);
  const sharingLabel = sharingLabelFor(workspace);
  const Icon = sharingIconFor(workspace.screen);

  useEffect(() => {
    setDismissed(false);
  }, [sharingLabel, workspace.screen]);

  const visible = Boolean(sharingLabel) && !dismissed;

  return {
    visible,
    sharingLabel,
    Icon,
    dismiss: () => setDismissed(true),
  };
}

type SharingStripProps = {
  onDismiss?: () => void;
};

/** Gemini-style context tab — full-width bar attached to the top of the composer card. */
export function LexCounselSharingStrip({ onDismiss }: SharingStripProps) {
  const { visible, sharingLabel, Icon, dismiss } = useLexCounselSharing();

  if (!visible || !sharingLabel) return null;

  const handleDismiss = () => {
    dismiss();
    onDismiss?.();
  };

  return (
    <div
      className="lexcounsel-sharing-bar flex w-full items-center gap-2.5 border-b border-[var(--counsel-border)] bg-[var(--counsel-sharing-bar-bg)] px-3.5 py-2.5"
      role="status"
      aria-live="polite"
    >
      <Icon
        className="h-4 w-4 shrink-0 text-[var(--counsel-text-secondary)]"
        strokeWidth={1.75}
        aria-hidden
      />
      <span className="min-w-0 flex-1 truncate text-[13px] leading-snug text-[var(--counsel-sharing-bar-text)]">
        {sharingLabel}
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 rounded-full p-1 text-[var(--counsel-text-muted)] transition-colors hover:bg-[var(--counsel-hover)] hover:text-[var(--counsel-text)]"
        aria-label="Dismiss shared context"
      >
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}

/** Footer below the composer — AI disclaimer. */
export default function LexCounselContextHints() {
  return (
    <div className="mt-2 px-1">
      <p className="text-center text-[11px] leading-snug text-[var(--counsel-text-muted)]">
        Lex Counsel can make mistakes.
      </p>
    </div>
  );
}
